import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteLayout, PageHero, GALLERY_ITEMS, GALLERY_CATEGORIES, useSEO } from "./shared";

export default function OurWork() {
  useSEO(
    "Our Work | Fire Protection Installations Across Tripura – SecureFlame",
    "A gallery of genuine SecureFlame fire protection installations — fire alarm, hydrant, sprinkler, fire pump and extinguisher systems installed across Tripura."
  );
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const items = filter === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.category === filter);

  return (
    <SiteLayout>
      <PageHero eyebrow="Our Work" title="Real Installations. Professional Execution." subtitle="Fire Protection Solutions Across Tripura." />

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="gallery-filters">
          {["All", ...GALLERY_CATEGORIES].map((c) => (
            <button key={c} onClick={() => setFilter(c)}
              className={`text-sm font-semibold px-4 py-2 rounded-full border transition-colors ${filter === c ? "bg-primary text-white border-primary" : "border-border text-secondary hover:border-primary/40"}`}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {items.map((g) => (
            <button key={g.n} onClick={() => setLightbox(g)} className="group text-left" data-testid={`gallery-item-${g.n}`}>
              <div className="aspect-square overflow-hidden rounded-sm border border-border bg-muted">
                <img src={`/installs/site-${g.n}.jpg`} alt={`SecureFlame ${g.caption} in Tripura`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
              <p className="mt-2 text-xs font-medium text-secondary">{g.caption}</p>
              <p className="text-[11px] text-muted-foreground">{g.category}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-muted">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-secondary">Need to Verify Our Experience?</h2>
          <p className="mt-4 text-muted-foreground">Contact SecureFlame to request company credentials, registrations, technical information or available project references.</p>
          <div className="mt-8 flex flex-wrap gap-3.5 justify-center">
            <Link to="/contact?type=contact&subject=credentials"><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold" data-testid="ourwork-credentials-btn">REQUEST CREDENTIALS</Button></Link>
            <Link to="/contact?type=site_visit"><Button size="lg" variant="outline" className="rounded-sm border-secondary/20 text-secondary hover:bg-secondary hover:text-white gap-2 font-semibold" data-testid="ourwork-sitevisit-btn">BOOK A SITE VISIT <ArrowRight size={16} /></Button></Link>
          </div>
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-5 right-5 text-white p-2" aria-label="Close" data-testid="gallery-lightbox-close"><X size={28} /></button>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={`/installs/site-${lightbox.n}.jpg`} alt={`SecureFlame ${lightbox.caption} in Tripura`} className="w-full max-h-[75vh] object-contain rounded-sm" />
            <p className="mt-3 text-white text-center text-sm font-medium">{lightbox.caption} — {lightbox.category}</p>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}
